import { lazy, Suspense } from "react"
import { Toaster } from "@/components/ui/toaster"
import { Toaster as Sonner } from "@/components/ui/sonner"
import { TooltipProvider } from "@/components/ui/tooltip"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom"
import { SEO } from "@/components/SEO"
import { ChatWidget } from "@/components/ChatWidget"

// Eagerly load the homepage for fastest initial paint
import Index from "./pages/Index"

// Lazy-load all other pages for code splitting
const Companies = lazy(() => import("./pages/Companies"))
const Connexr = lazy(() => import("./pages/companies/Connexr"))
const About = lazy(() => import("./pages/About"))
const Services = lazy(() => import("./pages/Services"))
const ManagedServicesAIOps = lazy(() => import("./pages/ManagedServicesAIOps"))
const AIConsultingAutomation = lazy(() => import("./pages/AIConsultingAutomation"))
const CloudInfrastructure = lazy(() => import("./pages/CloudInfrastructure"))
const HealthcareCaseStudy = lazy(() => import("./pages/HealthcareCaseStudy"))
const Events = lazy(() => import("./pages/Events"))
const Industries = lazy(() => import("./pages/Industries"))
const Careers = lazy(() => import("./pages/Careers"))
const RSATechCes2026 = lazy(() => import("./pages/RSATechCes2026"))
const RSAatCES2026 = lazy(() => import("./pages/RSAatCES2026"))
const RSATechHimss2026 = lazy(() => import("./pages/RSATechHimss2026"))
const NotFound = lazy(() => import("./pages/NotFound"))
const Contact = lazy(() => import("./pages/Contact"))
const FAQ = lazy(() => import("./pages/FAQ"))
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"))
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"))
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"))
const CustomerSecurityStatement = lazy(() => import("./pages/CustomerSecurityStatement"))
const HumanRightsStatement = lazy(() => import("./pages/HumanRightsStatement"))
const DoNotShare = lazy(() => import("./pages/DoNotShare"))
const CognitiveStrategy = lazy(() => import("./pages/CognitiveStrategy"))
const NewsInsights = lazy(() => import("./pages/NewsInsights"))
const LeadershipTeam = lazy(() => import("./pages/LeadershipTeam"))
const Partners = lazy(() => import("./pages/Partners"))
const Products = lazy(() => import("./pages/Products"))
const LeorixAI = lazy(() => import("./pages/LeorixAI"))
const NeuralInterfaces = lazy(() => import("./pages/NeuralInterfaces"))
const IntelligentProducts = lazy(() => import("./pages/IntelligentProducts"))
const AdaptiveEcosystems = lazy(() => import("./pages/AdaptiveEcosystems"))
const PredictiveFabrics = lazy(() => import("./pages/PredictiveFabrics"))
const AppliedIntelligence = lazy(() => import("./pages/AppliedIntelligence"))
const Lander = lazy(() => import("./pages/Lander"))
const Methodology = lazy(() => import("./pages/Methodology"))
const SmartMobility = lazy(() => import("./pages/SmartMobility"))
const IntelligentDigital = lazy(() => import("./pages/IntelligentDigital"))
const PredictiveSoftware = lazy(() => import("./pages/PredictiveSoftware"))
const DecentralizedIntelligence = lazy(() => import("./pages/DecentralizedIntelligence"))
const CognitiveEnterprise = lazy(() => import("./pages/CognitiveEnterprise"))
const GenerativeAI = lazy(() => import("./pages/GenerativeAI"))
const IoTEdge = lazy(() => import("./pages/IoTEdge"))
const CognitiveCloud = lazy(() => import("./pages/CognitiveCloud"))
const AlgorithmicCommerce = lazy(() => import("./pages/AlgorithmicCommerce"))
const AIEngineering = lazy(() => import("./pages/AIEngineering"))
const AIOps = lazy(() => import("./pages/AIOps"))
const IndustryDetail = lazy(() => import("./pages/IndustryDetail"))
const BlogsInsights = lazy(() => import("./pages/BlogsInsights"))
const AIOpsStrategy2026 = lazy(() => import("./pages/blogs/AIOpsStrategy2026"))
const HealthcareITTrends2026 = lazy(() => import("./pages/blogs/HealthcareITTrends2026"))
const JobApplication = lazy(() => import("./pages/JobApplication"))
const Technologies = lazy(() => import("./pages/Technologies"))
const FintechCaseStudy = lazy(() => import("./pages/FintechCaseStudy"))
const EdTechCaseStudy = lazy(() => import("./pages/EdTechCaseStudy"))
const TransportationCaseStudy = lazy(() => import("./pages/TransportationCaseStudy"))
const InsuranceCaseStudy = lazy(() => import("./pages/InsuranceCaseStudy"))
const RetailCaseStudy = lazy(() => import("./pages/RetailCaseStudy"))
const TechCaseStudy = lazy(() => import("./pages/TechCaseStudy"))
const ManufacturingCaseStudy = lazy(() => import("./pages/ManufacturingCaseStudy"))
const AIOPsCaseStudy = lazy(() => import("./pages/AIOPsCaseStudy"))
const AIManagedServices = lazy(() => import("./pages/AIManagedServices"))
const AIOpsConsulting = lazy(() => import("./pages/AIOpsConsulting"))
const EnterpriseAITransformation = lazy(() => import("./pages/EnterpriseAITransformation"))
const CloudAIServices = lazy(() => import("./pages/CloudAIServices"))
const GenerativeAISolutions = lazy(() => import("./pages/GenerativeAISolutions"))
const CustomAISolutions = lazy(() => import("./pages/CustomAISolutions"))

// Capability statements
const HealthcareCapabilityStatement = lazy(() => import("./pages/capability-statements/HealthcareCapabilityStatement").then(m => ({ default: m.HealthcareCapabilityStatement })))
const FintechCapabilityStatement = lazy(() => import("./pages/capability-statements/FintechCapabilityStatement").then(m => ({ default: m.FintechCapabilityStatement })))
const EdTechCapabilityStatement = lazy(() => import("./pages/capability-statements/EdTechCapabilityStatement").then(m => ({ default: m.EdTechCapabilityStatement })))
const TransportationCapabilityStatement = lazy(() => import("./pages/capability-statements/TransportationCapabilityStatement").then(m => ({ default: m.TransportationCapabilityStatement })))
const InsuranceCapabilityStatement = lazy(() => import("./pages/capability-statements/InsuranceCapabilityStatement").then(m => ({ default: m.InsuranceCapabilityStatement })))
const RetailCapabilityStatement = lazy(() => import("./pages/capability-statements/RetailCapabilityStatement").then(m => ({ default: m.RetailCapabilityStatement })))
const TechCapabilityStatement = lazy(() => import("./pages/capability-statements/TechCapabilityStatement").then(m => ({ default: m.TechCapabilityStatement })))
const ManufacturingCapabilityStatement = lazy(() => import("./pages/capability-statements/ManufacturingCapabilityStatement").then(m => ({ default: m.ManufacturingCapabilityStatement })))
const AIOpsCapabilityStatement = lazy(() => import("./pages/capability-statements/AIOpsCapabilityStatement").then(m => ({ default: m.AIOpsCapabilityStatement })))

const queryClient = new QueryClient()

const CanonicalHandler = () => {
  const location = useLocation()
  const path = location.pathname
  const canonicalPath = path === '/' ? '' : (path.endsWith('/') ? path : `${path}/`)
  const canonicalUrl = `https://rsatech.com${canonicalPath}`

  return (
    <Helmet>
      <link rel="canonical" href={canonicalUrl} />
    </Helmet>
  )
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <SEO
            title="RSA Tech Group | The AI Holding Company"
            description="RSA Tech Group is the AI holding company behind Connexr and LeoRix AI — building the companies that power the intelligent enterprise."
            image="/digital-strategy-impressive-bg.png"
          />
          <CanonicalHandler />
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/companies" element={<Companies />} />
              <Route path="/companies/connexr" element={<Connexr />} />
              <Route path="/companies/foundershub" element={<Navigate to="/companies" replace />} />
              <Route path="/about" element={<About />} />
              <Route path="/what-we-do" element={<Services />} />
              <Route
                path="/what-we-do/managed-services-aiops"
                element={<ManagedServicesAIOps />}
              />
              <Route
                path="/what-we-do/ai-transformation-automation"
                element={<AIConsultingAutomation />}
              />
              <Route
                path="/what-we-do/cloud-infrastructure"
                element={<CloudInfrastructure />}
              />
              <Route path="/technologies" element={<Technologies />} />
              <Route path="/products" element={<Products />} />
              <Route path="/leorix-ai" element={<LeorixAI />} />
              <Route path="/postwyse" element={<Navigate to="/companies" replace />} />
              <Route path="/promptpro" element={<Navigate to="/companies" replace />} />
              <Route path="/tradershub" element={<Navigate to="/companies" replace />} />
              <Route path="/smart-mobility" element={<SmartMobility />} />
              <Route
                path="/intelligent-digital-experiences"
                element={<IntelligentDigital />}
              />
              <Route path="/predictive-software" element={<PredictiveSoftware />} />
              <Route
                path="/decentralized-intelligence"
                element={<DecentralizedIntelligence />}
              />
              <Route
                path="/what-we-do/cognitive-enterprise"
                element={<CognitiveEnterprise />}
              />
              <Route path="/what-we-do/generative-ai" element={<GenerativeAI />} />
              <Route path="/iot-edge" element={<IoTEdge />} />
              <Route path="/cognitive-cloud" element={<CognitiveCloud />} />
              <Route
                path="/algorithmic-commerce"
                element={<AlgorithmicCommerce />}
              />
              <Route path="/ai-engineering" element={<AIEngineering />} />
              <Route path="/aiops" element={<AIOps />} />
              {/* SEO Keyword-Targeted Service Pages */}
              <Route path="/ai-managed-services" element={<AIManagedServices />} />
              <Route path="/aiops-consulting" element={<AIOpsConsulting />} />
              <Route path="/enterprise-ai-transformation" element={<EnterpriseAITransformation />} />
              <Route path="/cloud-ai-services" element={<CloudAIServices />} />
              <Route path="/generative-ai-solutions" element={<GenerativeAISolutions />} />
              <Route path="/cognitive-strategy" element={<CognitiveStrategy />} />
              <Route path="/neural-interfaces" element={<NeuralInterfaces />} />
              <Route
                path="/intelligent-products"
                element={<IntelligentProducts />}
              />
              <Route path="/adaptive-ecosystems" element={<AdaptiveEcosystems />} />
              <Route path="/predictive-fabrics" element={<PredictiveFabrics />} />
              <Route
                path="/applied-intelligence"
                element={<AppliedIntelligence />}
              />
              <Route path="/industries" element={<Industries />} />
              <Route path="/industries/aiops" element={<AIOPsCaseStudy />} />
              <Route path="/industries/aiops/capability-statement" element={<AIOpsCapabilityStatement />} />
              <Route path="/industries/healthcare" element={<HealthcareCaseStudy />} />
              <Route path="/industries/healthcare/capability-statement" element={<HealthcareCapabilityStatement />} />
              <Route path="/industries/fintech" element={<FintechCaseStudy />} />
              <Route path="/industries/fintech/capability-statement" element={<FintechCapabilityStatement />} />
              <Route path="/industries/edtech" element={<EdTechCaseStudy />} />
              <Route path="/industries/edtech/capability-statement" element={<EdTechCapabilityStatement />} />
              <Route path="/industries/transportation-airlines" element={<TransportationCaseStudy />} />
              <Route path="/industries/transportation-airlines/capability-statement" element={<TransportationCapabilityStatement />} />
              <Route path="/industries/insurance" element={<InsuranceCaseStudy />} />
              <Route path="/industries/insurance/capability-statement" element={<InsuranceCapabilityStatement />} />
              <Route path="/industries/retail-ecommerce" element={<RetailCaseStudy />} />
              <Route path="/industries/retail-ecommerce/capability-statement" element={<RetailCapabilityStatement />} />
              <Route path="/industries/tech-software" element={<TechCaseStudy />} />
              <Route path="/industries/tech-software/capability-statement" element={<TechCapabilityStatement />} />
              <Route path="/industries/hitech-manufacturing" element={<ManufacturingCaseStudy />} />
              <Route path="/industries/hitech-manufacturing/capability-statement" element={<ManufacturingCapabilityStatement />} />
              <Route path="/industries/:slug" element={<IndustryDetail />} />

              <Route path="/careers" element={<Careers />} />
              <Route path="/careers/apply" element={<JobApplication />} />
              <Route path="/news-insights" element={<NewsInsights />} />
              <Route path="/blogs-insights" element={<BlogsInsights />} />
              <Route path="/blogs/aiops-strategy-2026" element={<AIOpsStrategy2026 />} />
              <Route path="/blogs/healthcare-it-trends-2026" element={<HealthcareITTrends2026 />} />
              <Route path="/leadership-team" element={<LeadershipTeam />} />

              <Route path="/partners" element={<Partners />} />
              <Route path="/events" element={<Events />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="/customer-security-statement" element={<CustomerSecurityStatement />} />
              <Route path="/human-rights-statement" element={<HumanRightsStatement />} />
              <Route path="/do-not-share" element={<DoNotShare />} />
              <Route path="/lander" element={<Lander />} />
              <Route path="/methodology" element={<Methodology />} />
              <Route path="/what-we-build/custom-ai-solutions" element={<CustomAISolutions />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="/events/girish-ces-2026" element={<Navigate to="/events/rsa-ces-2026" replace />} />
              <Route path="/events/rsa-ces-2026-vision" element={<RSATechCes2026 />} />
              <Route path="/events/rsa-ces-2026" element={<RSAatCES2026 />} />
              <Route path="/events/rsa-himss-2026" element={<RSATechHimss2026 />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <ChatWidget />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
)

export default App
